import React, { useState } from 'react';
import axios, { API } from '../lib/api';
import { Button } from './ui/button';
import { Sparkles, RefreshCw, Flag } from 'lucide-react';
import { toast } from 'sonner';

const AIFeedbackSection = ({ taskId, savedReflection, initialFeedback }) => {
  const [aiFeedback, setAiFeedback] = useState(initialFeedback);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reported, setReported] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setReported(false);
    try {
      const resp = await axios.post(`${API}/tasks/${taskId}/feedback`);
      setAiFeedback(resp.data.ai_feedback);
      toast.success('AI feedback ready!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not generate feedback right now.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReport = async () => {
    try {
      await axios.post(`${API}/tasks/${taskId}/report-feedback`);
      setReported(true);
      toast.success('Reported — the admin will review this response.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report.');
    }
  };

  if (!savedReflection) return null;

  return (
    <div className="pt-1">
      {!aiFeedback && !isGenerating && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerate}
          className="border-justb-primary/30 text-justb-primary hover:bg-justb-primary/5"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          Get AI feedback
        </Button>
      )}

      {isGenerating && (
        <div className="flex items-center gap-2 text-sm text-justb-primary">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-justb-primary"></div>
          <span>Your coach is reading your reflection…</span>
        </div>
      )}

      {aiFeedback && !isGenerating && (
        <div className="rounded-lg border border-justb-primary/20 bg-gradient-to-br from-justb-primary/5 via-white to-amber-50/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-justb-primary/10 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-justb-primary" />
              </div>
              <span className="text-sm font-semibold text-justb-primary">Your coach says</span>
            </div>
            <div className="flex items-center gap-2">
              {!reported ? (
                <button
                  type="button"
                  onClick={handleReport}
                  className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors"
                  title="Report inappropriate response"
                  data-testid={`report-feedback-${taskId}`}
                >
                  <Flag className="h-3 w-3" />
                  Report
                </button>
              ) : (
                <span className="text-xs text-gray-400">Reported</span>
              )}
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating}
                className="text-xs text-gray-500 hover:text-justb-primary flex items-center gap-1 transition-colors"
                title="Regenerate feedback"
              >
                <RefreshCw className="h-3 w-3" />
                Regenerate
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{aiFeedback}</p>
        </div>
      )}
    </div>
  );
};

export default AIFeedbackSection;
